/**
 * Portal host → API host. The portal must call a same-site (drata.net) API so the
 * Access cookie is sent; CI still bakes the .com API into VITE_VENDOR_API_URL.
 */
export const NET_PORTAL_API_HOSTS: ReadonlyMap<string, string> = new Map([
    ['vendor-management-dev.drata.net', 'https://vendor-global-api-dev.drata.net'],
    ['vendor-management-qa.drata.net', 'https://vendor-global-api-qa.drata.net'],
    ['vendor-management.drata.net', 'https://vendor-global-api.drata.net'],
]);

const DEFAULT_API_BASE = 'http://localhost:3030';

/**
 * Resolve the API origin for the page's hostname. A mapped drata.net portal host
 * gets its .net API. Any other host (local dev, previews) keeps the build-time
 * `VITE_VENDOR_API_URL`.
 */
export function resolveApiBase(
    hostname: string | undefined,
    buildTimeApiBase: string | undefined,
): string {
    const netApiBase =
        hostname === undefined ? undefined : NET_PORTAL_API_HOSTS.get(hostname.toLowerCase());

    return netApiBase ?? buildTimeApiBase ?? DEFAULT_API_BASE;
}

export const API_BASE = resolveApiBase(
    typeof window === 'undefined' ? undefined : window.location.hostname,
    import.meta.env.VITE_VENDOR_API_URL,
);

/**
 * Fetch wrapper for the vendor-global-service API.
 *
 * Auth is handled at the edge: the vendor-management hostname sits behind a
 * Cloudflare Access application backed by Drata's Okta tenant (same pattern
 * as cosmos.drata.com / admin-*.drata.com). After the user signs in once,
 * the API host (on the same registrable domain as the portal, drata.net; see
 * `NET_PORTAL_API_HOSTS`) carries its own `CF_Authorization`
 * cookie, which the browser sends with these cross-origin, same-site requests
 * as long as we include credentials.
 *
 * The browser never sees a token; this wrapper just opts every request into
 * cookie/credential forwarding.
 */
export async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
    const headers = new Headers(options?.headers);

    // Only attach Content-Type when there's a body to describe. `application/json`
    // is not a CORS-safelisted value, so setting it on every request (including
    // bodyless GETs) forces the browser to issue a per-URL preflight `OPTIONS`
    // round-trip. With the search debouncing in VendorListView and offset-based
    // pagination, each keystroke / page becomes a unique URL, defeating the
    // preflight cache and doubling latency on first hit.
    if (options?.body !== undefined && options.body !== null) {
        headers.set('Content-Type', 'application/json');
    }

    const response = await fetch(`${API_BASE}${path}`, {
        ...options,
        credentials: 'include',
        headers,
    });

    if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');

        throw new Error(`API error ${response.status.toString()}: ${errorText}`);
    }

    return response.json();
}
